// P3-4 — pestañas de gestión del Catálogo: Categorías, Marcas y Colecciones.
// Un solo componente parametrizado por `tipo`: lista con conteo, crear y
// renombrar; las categorías además eligen padre. Escribe en WooCommerce por los
// endpoints /api/{categories,brands,collections} (taxonomías nativas: marca =
// `brands`, colección = etiqueta con slug `coleccion-*`). Renombrar un término
// NO toca los productos que lo usan — sólo el término (lo verifica el e2e).
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Check, X } from 'lucide-react';
import Button from './ui/Button';
import { useToast } from './ToastManager';
import { propsSoloLectura } from '../utils/soloLectura';
import {
  listarCategorias, crearCategoria, renombrarCategoria,
  listarMarcas, crearMarca, renombrarMarca,
  listarColecciones, crearColeccion, renombrarColeccion,
} from '../utils/productApi';
import { decodeHtml } from '../utils/decodeHtml';
import './CatalogoGestionTab.css';

// Cada `tipo` difiere SÓLO en los datos: cómo se listan sus ítems, cómo se crean
// y renombran, y si llevan padre. Todo lo demás (lista, formulario, edición en
// línea) es común — un solo camino, no tres copias.
const CFG = {
  categorias: {
    singular: 'categoría', plural: 'categorías', conPadre: true,
    listar: listarCategorias,
    crear: (nombre, parent) => crearCategoria(nombre, parent),
    renombrar: (id, nombre, parent) => renombrarCategoria(id, { name: nombre, parent }),
    vacio: 'No hay categorías todavía.',
  },
  marcas: {
    singular: 'marca', plural: 'marcas', conPadre: false,
    listar: listarMarcas,
    crear: (nombre) => crearMarca(nombre),
    renombrar: (id, nombre) => renombrarMarca(id, nombre),
    vacio: 'No hay marcas todavía.',
  },
  colecciones: {
    singular: 'colección', plural: 'colecciones', conPadre: false,
    listar: listarColecciones,
    crear: (nombre) => crearColeccion(nombre),
    renombrar: (id, nombre) => renombrarColeccion(id, nombre),
    // el GET de colecciones usa `nombre` (el visible, sin «Colección: »).
    campoNombre: 'nombre',
    vacio: 'No hay colecciones todavía. Créalas aquí para ofrecerlas en la ficha.',
  },
};

export default function CatalogoGestionTab({ tipo, soloLectura, onCambio }) {
  const cfg = CFG[tipo];
  const toast = useToast();
  const showToast = (toast && toast.showToast) || (() => {});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoPadre, setNuevoPadre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editPadre, setEditPadre] = useState('');

  const nombreDe = useCallback((it) => decodeHtml(String(it[cfg.campoNombre || 'name'] || '')), [cfg.campoNombre]);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await cfg.listar();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    const nombre = nuevoNombre.trim();
    if (!nombre || guardando) return;
    setGuardando(true);
    try {
      await cfg.crear(nombre, cfg.conPadre ? (Number(nuevoPadre) || 0) : undefined);
      showToast({ message: `${cap(cfg.singular)} creada`, variant: 'success' });
      setNuevoNombre(''); setNuevoPadre('');
      await cargar();
      if (onCambio) onCambio();
    } catch (e) {
      showToast({ message: e.message || 'No se pudo crear', variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const empezarEdicion = (it) => {
    setEditId(it.id);
    setEditNombre(nombreDe(it));
    setEditPadre(cfg.conPadre ? String(it.parent || '') : '');
  };
  const cancelarEdicion = () => { setEditId(null); setEditNombre(''); setEditPadre(''); };

  const guardarEdicion = async () => {
    const nombre = editNombre.trim();
    if (!nombre || guardando) return;
    setGuardando(true);
    try {
      await cfg.renombrar(editId, nombre, cfg.conPadre ? (Number(editPadre) || 0) : undefined);
      showToast({ message: `${cap(cfg.singular)} actualizada`, variant: 'success' });
      cancelarEdicion();
      await cargar();
      if (onCambio) onCambio();
    } catch (e) {
      showToast({ message: e.message || 'No se pudo guardar', variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  // Opciones de padre (categorías): todas menos la que se edita (no puede ser su
  // propio padre). Nombre decodificado para el <select>.
  const opcionesPadre = cfg.conPadre
    ? items.filter((it) => it.id !== editId).map((it) => ({ id: it.id, name: nombreDe(it) }))
    : [];

  return (
    <div className="cgt">
      <div className="cgt-nuevo">
        <input
          type="text"
          value={nuevoNombre}
          onChange={(e) => setNuevoNombre(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') crear(); }}
          placeholder={`Nueva ${cfg.singular}…`}
          aria-label={`Nombre de la ${cfg.singular}`}
          {...propsSoloLectura(soloLectura)}
        />
        {cfg.conPadre && (
          <select value={nuevoPadre} onChange={(e) => setNuevoPadre(e.target.value)} aria-label="Categoría padre" {...propsSoloLectura(soloLectura)}>
            <option value="">Sin padre (principal)</option>
            {items.map((it) => <option key={it.id} value={it.id}>{nombreDe(it)}</option>)}
          </select>
        )}
        <Button variant="primary" icon={<Plus size={16} />} onClick={crear} disabled={!nuevoNombre.trim() || guardando} {...propsSoloLectura(soloLectura)}>
          Crear
        </Button>
      </div>

      {loading ? (
        <p className="cgt-muted">Cargando {cfg.plural}…</p>
      ) : error ? (
        <p className="cgt-error">{error}</p>
      ) : items.length === 0 ? (
        <p className="cgt-muted">{cfg.vacio}</p>
      ) : (
        <table className="cgt-tabla">
          <thead>
            <tr>
              <th>Nombre</th>
              {cfg.conPadre && <th>Padre</th>}
              <th className="cgt-num">Productos</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const padre = cfg.conPadre && it.parent ? items.find((x) => x.id === it.parent) : null;
              return (
                <tr key={it.id}>
                  {editId === it.id ? (
                    <>
                      <td>
                        <input
                          type="text" value={editNombre} autoFocus
                          onChange={(e) => setEditNombre(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') guardarEdicion(); if (e.key === 'Escape') cancelarEdicion(); }}
                          aria-label="Nuevo nombre"
                        />
                      </td>
                      {cfg.conPadre && (
                        <td>
                          <select value={editPadre} onChange={(e) => setEditPadre(e.target.value)} aria-label="Nuevo padre">
                            <option value="">Sin padre (principal)</option>
                            {opcionesPadre.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </td>
                      )}
                      <td className="cgt-num">{it.count ?? 0}</td>
                      <td className="cgt-acc">
                        <button className="cgt-iconbtn" onClick={guardarEdicion} disabled={guardando} aria-label="Guardar"><Check size={16} /></button>
                        <button className="cgt-iconbtn" onClick={cancelarEdicion} aria-label="Cancelar"><X size={16} /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{nombreDe(it)}</td>
                      {cfg.conPadre && <td className="cgt-muted">{padre ? nombreDe(padre) : '—'}</td>}
                      <td className="cgt-num">{it.count ?? 0}</td>
                      <td className="cgt-acc">
                        <button className="cgt-iconbtn" onClick={() => empezarEdicion(it)} aria-label={`Renombrar ${nombreDe(it)}`} {...propsSoloLectura(soloLectura)}><Pencil size={15} /></button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
